import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CheckCircle, ArrowRight, Building2, LogIn, Shield, Users,
  CreditCard, Copy, ExternalLink,
} from 'lucide-react';

interface LocationState {
  clinicName?: string;
  adminEmail?: string;
  stripeOnboardingUrl?: string;
}

const ClinicOnboardingSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const clinicName = state?.clinicName || 'Your Clinic';
  const adminEmail = state?.adminEmail || 'your email';
  const stripeOnboardingUrl = state?.stripeOnboardingUrl || null;
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = async () => {
    if (!stripeOnboardingUrl) return;
    try {
      await navigator.clipboard.writeText(stripeOnboardingUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // fallback ignored
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle radial gradient overlay */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(45,106,120,0.06),transparent_70%)] pointer-events-none" />

      {/* Decorative Circles */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-teal/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-brand-teal/10 rounded-full"
          style={{
            left: `${20 + i * 15}%`,
            top: `${30 + (i % 3) * 20}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            duration: 3 + i * 0.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-2xl"
      >
        <Card className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-lg">
          <CardHeader className="bg-white border-b border-gray-100 p-8">
            <div className="flex items-center space-x-4">
              <motion.div
                className="w-20 h-20 bg-brand-teal/10 rounded-2xl flex items-center justify-center"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <CheckCircle className="h-12 w-12 text-brand-teal" />
              </motion.div>
              <div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <CardTitle className="text-3xl text-gray-900">Onboarding Completed!</CardTitle>
                  <p className="text-gray-500 mt-2 text-lg">Your clinic is ready to go</p>
                </motion.div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Clinic Created Confirmation */}
              <motion.div
                className="bg-brand-teal/10 border border-brand-teal/20 rounded-xl p-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-teal rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-brand-teal/25">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg mb-2">
                      {clinicName} is Ready!
                    </h3>
                    <p className="text-gray-500">
                      Your clinic has been successfully created and your admin account is active.
                      If you were not redirected to the dashboard, use the button below to log in with your email and password.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Account Ready Message */}
              <motion.div
                className="bg-blue-50 border border-blue-200 rounded-xl p-6"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, type: 'spring' }}
              >
                <div className="flex items-start space-x-4">
                  <motion.div
                    className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Shield className="h-7 w-7 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">
                      Your Account is Ready
                    </h3>
                    <p className="text-gray-600 mb-3">
                      You can log in with your email <span className="font-semibold text-blue-600">{adminEmail}</span> and
                      the password you created during onboarding.
                    </p>
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      <LogIn className="h-4 w-4 mr-2" />
                      Click the button below to log in
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Stripe Payment Setup Link */}
              {stripeOnboardingUrl && (
                <motion.div
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.55, type: 'spring' }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-lg mb-1">Payment Setup</h3>
                      <p className="text-gray-500 text-sm mb-3">
                        Complete your payment setup by visiting this link. It takes about 5 minutes on
                        Stripe&apos;s secure page.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          value={stripeOnboardingUrl}
                          readOnly
                          className="text-xs h-9 bg-gray-50 border-gray-200 text-gray-700"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 h-9 gap-1.5 border-gray-200 text-gray-700 hover:bg-gray-100"
                          onClick={handleCopyLink}
                        >
                          {linkCopied ? (
                            <><CheckCircle className="h-3.5 w-3.5" /> Copied</>
                          ) : (
                            <><Copy className="h-3.5 w-3.5" /> Copy</>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 h-9 gap-1.5 border-gray-200 text-gray-700 hover:bg-gray-100"
                          onClick={() => window.open(stripeOnboardingUrl, '_blank')}
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Open
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* What's Next */}
              <motion.div
                className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-brand-teal rounded-xl flex items-center justify-center">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900">What's Next?</h3>
                </div>
                <div className="space-y-3 ml-2">
                  {[
                    { num: 1, text: 'Log in to your clinic dashboard' },
                    { num: 2, text: 'Complete your clinic profile settings' },
                    { num: 3, text: 'Add practitioners to your clinic' },
                    { num: 4, text: 'Start managing your practice!' },
                  ].map((step, index) => (
                    <motion.div
                      key={step.num}
                      className="flex items-center space-x-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                    >
                      <div className="w-7 h-7 bg-brand-teal rounded-full flex items-center justify-center flex-shrink-0 shadow-md shadow-brand-teal/25">
                        <span className="text-white font-semibold text-xs">{step.num}</span>
                      </div>
                      <p className="text-gray-600 text-sm">{step.text}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Support Note */}
              <motion.div
                className="border-t border-gray-200 pt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                <p className="text-gray-500 text-sm text-center">
                  Need help getting started? Contact our support team for assistance.
                </p>
              </motion.div>

              {/* Action Button */}
              <motion.div
                className="pt-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full h-14 rounded-full bg-brand-teal hover:bg-brand-teal/90 font-medium text-white text-lg shadow-lg shadow-brand-teal/25"
                    size="lg"
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Log In to Your Clinic
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ClinicOnboardingSuccess;
